import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import AppApi from 'src/api/AppApi';
import PlatformApi from 'src/api/PlatformApi';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import {
  Account as ServerAccount,
  Balance as ServerBalance,
  BalanceTypes,
  PaymentCredentials as ServerPaymentCredentials,
  PaymentToken,
  PayrollDeductInfo,
  ProductData,
} from 'src/types/serverTypes/Account';
import PaymentCredentialsBuilder, {
  PaymentCredentials,
} from 'src/models/PaymentCredentials';
import AccountApi from 'src/api/AccountApi';
import CreditCardBuilder, {CreditCard} from 'src/models/CreditCard';
import AccountBuilder, {Account, defaultAccount} from 'src/models/Account';
import {AppDispatch, RootState} from '../store';
import BalanceBuilder, {Balance} from 'src/models/Balance';
import AccountConstants from 'src/constants/AccountConstants';
import TimeUtils from 'src/services/TimeUtils';
import PayrollDeductInformationBuilder, {
  PayrollDeductInformation,
} from 'src/models/PayrollDeductInformation';
import BalanceChangedEvent from 'src/logging/BalanceChangedEvent';
import {hideSpinner, showSpinner} from './screenSlice';
import Localized from 'src/constants/AppStrings';
import PersistentStore from 'src/services/PersistentStoreService';
import {savedReceipt} from 'src/components/elements/DeepLinkFunctions';
import {alertError, alertSuccess} from 'src/components/helpers/AlertHelper';
import moment from 'moment';
import uuid from 'src/nativeModules/UUID';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import Util from '../../Util';
import Logger from 'src/logging/Logger';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import {getDescriber} from 'src/components/elements/descriptor/DescriptorType';
import AccountStore from '../../stores/AccountStore';
import LocationFeatures, {Sfecfg} from 'src/models/LocationFeatures';

export interface IAccountState {
  paymentCredentials: PaymentCredentials;
  creditCards: Array<CreditCard>;
  canAddFunds: boolean;
  account: Account;
  defaultPaymentToken: string;
  defaultNonNfcPaymentToken: string;
  payrollDeductInformation: PayrollDeductInformation | null;
  savedReceipt: savedReceipt | null;
  beaconPopup: boolean;
  productRecommendations: ProductData[];
  //check
  accountBalanceId: string;
  isHomeLocationSma: boolean;
  payrollAutoComplete?: boolean;
  isPaymentFunding: boolean;
  lastChanged?: string;
  lastDeletedCardWasDefault?: boolean;
  pinRequired?: boolean;
  privacyVersionResponse?: {
    status: string;
    version: string;
    type: string;
  } | null;
}

const defaultPaymentCredentials = {
  paymentAvailable: false,
  applePayAvailable: false,
  googlePayAvailable: false,
} as PaymentCredentials;

export const initialState: IAccountState = {
  paymentCredentials: defaultPaymentCredentials,
  creditCards: [],
  productRecommendations: [],
  canAddFunds: false,
  account: defaultAccount,
  defaultPaymentToken: '',
  defaultNonNfcPaymentToken: '',
  payrollDeductInformation: null,
  savedReceipt: null,
  beaconPopup: false,
  //check
  accountBalanceId: '',
  isHomeLocationSma: false,
  isPaymentFunding: false,
};

const getPayrollDeductGroupId = async (account: Account) => {
  let payrollDeductGroupId = account.payrollDeductGroupId;
  const locationId = account.locationId;

  if (!payrollDeductGroupId && locationId) {
    const payrollDeductGroup = (await PlatformApi.fetchPayrollDeductGroup(
      locationId,
    )) as {payrollDeductGroupId?: string};
    Logger.Log.LogAPIEvent(
      'PlatformAPI',
      'FetchPayrollDeductGroup',
      JSON.stringify({locationId}),
      JSON.stringify(payrollDeductGroup),
    );

    if (payrollDeductGroup) {
      payrollDeductGroupId = payrollDeductGroup.payrollDeductGroupId;
    }
  }

  return payrollDeductGroupId;
};

export const payrollDeductOptInChanged = createAsyncThunk<
  boolean,
  boolean,
  {
    state: RootState;
    dispatch: AppDispatch;
  }
>(
  'account/payrollDeductOptInChanged',
  async (optIn: boolean, {getState, dispatch}) => {
    const account = getState().account.account;
    const payrollDeductGroupId = await getPayrollDeductGroupId(account);
    if (payrollDeductGroupId) {
      await AccountApi.setPayrollDeductOptIn(
        account.id,
        optIn,
        payrollDeductGroupId,
      );

      if (!account.hasPayrollBalance && optIn) {
        dispatch(fetchAccountBalances(account.id));
      }
    }
    return optIn;
  },
);

export const fetchAccountBalances = createAsyncThunk<
  Array<Balance>,
  string,
  {
    state: RootState;
    dispatch: AppDispatch;
  }
>('account/fetchAccountBalances', async (accountId: string, {getState}) => {
  if (AccountStore.isDemo()) {
    return require('../../demo/LatestBalances.json');
  }
  try {
    const {account} = getState().account;
    const balances = (await AccountApi.fetchBalances(
      accountId || account.id,
    )) as Array<ServerBalance>;
    Logger.Log.LogAPIEvent(
      'AccountAPI',
      'FetchBalances',
      JSON.stringify({accountId}),
      JSON.stringify(balances),
    );
    const mostRecentBalanceUpdate = balances
      .map((balance) => balance.lastUpdated)
      .sort((a, b) => moment(a).diff(moment(b)))[0];
    if (
      moment(account.accountBalancesLastUpdated).isAfter(
        moment(mostRecentBalanceUpdate),
      )
    ) {
      account.accountBalancesLastUpdated = moment().toString();
    }
    return balances
      ? balances.map((balance) => BalanceBuilder.build(balance))
      : account.balances;
  } catch (error) {
    CrashlyticsEvents.log(
      'Exception',
      'accountSlice:fetchAccountBalances',
      generateErrorMessage(error),
    );
    Events.Error.trackEvent(
      'Exception',
      'accountSlice:fetchAccountBalances',
      generateErrorMessage(error),
    );
    Logger.Log.LogAPIEvent(
      'AccountAPI',
      'FetchBalances',
      JSON.stringify({accountId}),
      JSON.stringify(error),
    );
  }
});

export const fetchPaymentCredentials = createAsyncThunk(
  'account/fetchPaymentCredentials',
  async (accountId: string) => {
    try {
      if (!accountId || accountId === '-1') {
        throw new Error('Invalid Account ID');
      }
      const serverCredentials = (await AppApi.getPaymentCredentials(
        accountId,
      )) as ServerPaymentCredentials;
      FirebaseAnalytic.trackEvent(
        'accountSlice:fetchPaymentCredentials',
        'accountSlice',
        {
          serverCredentials,
          accountId,
          url: serverCredentials.url,
          key: serverCredentials.key,
          test: serverCredentials.test,
          merchantId: serverCredentials.merchantId,
          type: serverCredentials.type,
          useConnect: serverCredentials.useConnect,
        },
      );
      Logger.Log.LogAPIEvent(
        'AppAPI',
        'GetPaymentCredentials',
        JSON.stringify({accountId}),
        JSON.stringify(serverCredentials),
      );
      return await PaymentCredentialsBuilder.build(serverCredentials);
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'accountSlice:fetchPaymentCredentials',
        generateErrorMessage(error),
        guid,
        {
          accountId,
        },
      );
      Events.Error.trackEvent(
        'Exception',
        'accountSlice:fetchPaymentCredentials',
        generateErrorMessage(error),
        guid,
        {
          accountId,
        },
      );
      Logger.Log.LogAPIEvent(
        'AppAPI',
        'GetPaymentCredentials',
        JSON.stringify({accountId}),
        JSON.stringify(error),
      );
    }
  },
);

export const fetchAccountBalanceCreditCards = createAsyncThunk(
  'account/fetchAccountBalanceCreditCards',
  async (data: {accountId: string; accountBalanceId: string}) => {
    try {
      const {accountId, accountBalanceId} = data;
      const paymentTokens = (await AccountApi.fetchTokens(
        accountId,
        accountBalanceId,
      )) as Array<PaymentToken>;
      Logger.Log.LogAPIEvent(
        'AccountAPI',
        'FetchTokens',
        JSON.stringify({accountId, accountBalanceId}),
        JSON.stringify(paymentTokens),
      );
      return paymentTokens.map((token) => CreditCardBuilder.build(token));
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'accountSlice:fetchAccountBalanceCreditCards',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'accountSlice:fetchAccountBalanceCreditCards',
        generateErrorMessage(error),
      );
    }
  },
);

export const deleteCard = createAsyncThunk(
  'account/deleteCard',
  async (data: {
    accountId: string;
    accountBalanceId: string;
    token: string;
    isDefault?: boolean;
  }) => {
    try {
      const {accountId, accountBalanceId, token, isDefault} = data;
      await AppApi.deleteCreditCard(accountId, token, accountBalanceId);
      Logger.Log.LogAPIEvent(
        'AppAPI',
        'DeleteCreditCard',
        JSON.stringify({accountId, accountBalanceId, token}),
        'success',
      );
      const paymentTokens = (await AccountApi.fetchTokens(
        accountId,
        accountBalanceId,
      )) as Array<PaymentToken>;
      return {
        creditCards: paymentTokens.map((t) => CreditCardBuilder.build(t)),
        isDefault: isDefault || false,
      };
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'accountSlice:deleteCard',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'accountSlice:deleteCard',
        generateErrorMessage(error),
        guid,
      );
      throw error;
    }
  },
);

export const setDefaultPaymentToken = createAsyncThunk(
  'account/setDefaultPaymentToken',
  async (data: {
    accountId: string;
    accountBalanceId: string;
    token: string;
  }) => {
    try {
      const {accountId, accountBalanceId, token} = data;
      await AccountApi.updateDefaultToken(accountId, accountBalanceId, token);
      Logger.Log.LogAPIEvent(
        'AccountAPI',
        'UpdateDefaultToken',
        JSON.stringify({accountId, accountBalanceId, token}),
        'success',
      );
      return token;
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'accountSlice:setDefaultPaymentToken',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'accountSlice:setDefaultPaymentToken',
        generateErrorMessage(error),
        guid,
      );
      throw error;
    }
  },
);

export const updatePin = createAsyncThunk(
  'account/updatePin',
  async (
    data: {
      accountId: string;
      pin: string;
      pinRequired: boolean;
      username?: string;
    },
    {getState},
  ) => {
    try {
      await AccountApi.setPin(data.accountId, data.pin);
      Logger.Log.LogAPIEvent(
        'AccountAPI',
        'SetPin',
        JSON.stringify({accountId: data.accountId}),
        'success',
      );

      const state = getState() as RootState;
      const username = data.username || state.account.account?.userName || '';
      await PersistentStore.setPinRequired(data.pinRequired, username);

      return {pinRequired: data.pinRequired};
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'accountSlice:updatePin',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'accountSlice:updatePin',
        generateErrorMessage(error),
        guid,
      );
      throw error;
    }
  },
);

export const acceptPrivacyVersion = createAsyncThunk(
  'account/acceptPrivacy',
  async (data: {accountId: string; version: string; date: string}) => {
    try {
      await AccountApi.acceptPrivacy(data.accountId, data.version, data.date);
      Logger.Log.LogAPIEvent(
        'AccountAPI',
        'AcceptPrivacy',
        JSON.stringify({accountId: data.accountId, version: data.version}),
        'success',
      );
      return {
        version: data.version,
        status: 'ok',
        type: 'DEFAULT',
      };
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'accountSlice:acceptPrivacy',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'accountSlice:acceptPrivacy',
        generateErrorMessage(error),
        guid,
      );
      throw error;
    }
  },
);

export const updateProfile = createAsyncThunk(
  'account/updateProfile',
  async (
    data: {
      accountId: string;
      info: {
        firstName?: string;
        lastName?: string;
        gender?: string;
        birthMonth?: string;
        birthYear?: string;
        jobTitle?: string;
        locale?: string;
      };
    },
    {getState},
  ) => {
    try {
      const infoToUpdate = {
        ...data.info,
        birthMonth: data.info.birthMonth
          ? Number(data.info.birthMonth)
          : undefined,
        birthYear: data.info.birthYear
          ? Number(data.info.birthYear)
          : undefined,
      };
      const response = await AccountApi.updateAccount(
        infoToUpdate,
        data.accountId,
      );
      Logger.Log.LogAPIEvent(
        'AccountAPI',
        'UpdateAccount',
        JSON.stringify({accountId: data.accountId, info: data.info}),
        JSON.stringify(response),
      );

      const state = getState() as RootState;
      const account = state.account.account;
      if (account) {
        const accountInfo = {
          Email: account.email,
          FirstName: data.info.firstName || account.firstName || '',
          LastName: data.info.lastName || account.lastName || '',
          firstName: data.info.firstName || account.firstName || '',
          lastName: data.info.lastName || account.lastName || '',
          gender: data.info.gender || account.demographics?.gender || '',
          birthMonth: data.info.birthMonth || account.demographics?.birthMonth,
          birthYear: data.info.birthYear || account.demographics?.birthYear,
          jobTitle: data.info.jobTitle || account.demographics?.jobTitle || '',
          locale: data.info.locale || account.locale || '',
        };
        await PersistentStore.setAccountInfo(accountInfo);
      }

      return data.info;
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'accountSlice:updateProfile',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'accountSlice:updateProfile',
        generateErrorMessage(error),
        guid,
      );
      throw error;
    }
  },
);

export const fetchAccount = createAsyncThunk<
  | {
      account: Account;
      creditCards: Array<CreditCard>;
      payrollDeductInformation: PayrollDeductInformation | null;
      pinRequired: boolean;
    }
  | undefined,
  string,
  {
    state: RootState;
    dispatch: AppDispatch;
  }
>('account/fetchAccount', async (accountId: string, {getState}) => {
  if (AccountStore.isDemo()) {
    return require('../../demo/AccountFetchAccount.json');
  }
  try {
    const {account} = getState().account;
    const serverAccount: ServerAccount = await AccountApi.fetchAccount(
      accountId,
    );

    // Protection: If serverAccount is invalid/empty (network error), throw error to prevent empty data
    if (
      !serverAccount ||
      !serverAccount.accountId ||
      !serverAccount.email ||
      serverAccount.accountId === '' ||
      serverAccount.email === ''
    ) {
      throw new Error(
        'Invalid server account response - network error or empty data',
      );
    }

    Logger.Log.LogAPIEvent(
      'AccountAPI',
      'FetchAccount',
      JSON.stringify({accountId}),
      JSON.stringify(serverAccount),
    );
    let accountModel: Account;
    if (
      !account.lastUpdated ||
      moment(serverAccount.lastUpdated).isAfter(moment(account.lastUpdated))
    ) {
      accountModel = AccountBuilder.build(serverAccount);
      if (
        !accountModel.id ||
        !accountModel.email ||
        accountModel.id === '' ||
        accountModel.email === ''
      ) {
        throw new Error(
          'AccountBuilder created empty account - network error or invalid data',
        );
      }
    } else {
      accountModel = {
        ...account,
        pantryGroup: serverAccount.pantryGroup ?? account.pantryGroup ?? null,
      };
    }
    let creditCards: Array<CreditCard> = [];
    if (accountModel.defaultBalance) {
      const paymentTokens = (await AccountApi.fetchTokens(
        accountId,
        accountModel.defaultBalance.id,
      )) as Array<PaymentToken>;
      Logger.Log.LogAPIEvent(
        'AccountAPI',
        'FetchTokens',
        JSON.stringify({
          accountId,
          accountBalanceId: accountModel.defaultBalance.id,
        }),
        JSON.stringify(paymentTokens),
      );
      creditCards = paymentTokens.map((token) =>
        CreditCardBuilder.build(token),
      );
    }
    let payrollDeductInformation = initialState.payrollDeductInformation;
    if (accountModel.payrollDeductGroupId || accountModel.locationId) {
      // if (accountModel.hasPayrollBalance) { /** removing to show payroll details screen the first time pde  is linked via payroll import */
      const payrollData: PayrollDeductInfo =
        await PlatformApi.retrievePayrollDeductData(
          TimeUtils.getCurrentServerDate(),
          accountModel.payrollDeductGroupId,
          accountModel.locationId,
        );
      Logger.Log.LogAPIEvent(
        'PlatformAPI',
        'RetrievePayrollDeductData',
        JSON.stringify({
          locationId: accountModel.locationId,
          payrollDeductGroupId: accountModel.payrollDeductGroupId,
        }),
        JSON.stringify(payrollData),
      );
      payrollDeductInformation = PayrollDeductInformationBuilder.build(
        payrollData,
        accountModel.balances.find(
          (balance) => balance.type === BalanceTypes.Payroll,
        ),
      );
      // }
    }

    // Load pinRequired from PersistentStore
    let pinRequired = false;
    if (accountModel.userName) {
      const userStartupData = await PersistentStore.getUserStartupData(
        accountModel.userName,
      );
      pinRequired = userStartupData.pinRequired || false;
    }

    return {
      account: accountModel,
      creditCards,
      payrollDeductInformation,
      pinRequired,
    };
  } catch (error) {
    CrashlyticsEvents.log(
      'Exception',
      'accountSlice:fetchAccount',
      generateErrorMessage(error),
    );
    Events.Error.trackEvent(
      'Exception',
      'accountSlice:fetchAccount',
      generateErrorMessage(error),
    );
    Logger.Log.LogAPIEvent(
      'AccountAPI',
      'FetchAccount',
      JSON.stringify({accountId}),
      JSON.stringify(error),
    );
  }
});

export const scanGiftCard = createAsyncThunk<
  {[index: string]: number | string | boolean},
  {code: string; callback: () => void},
  {
    state: RootState;
    dispatch: AppDispatch;
  }
>('account/scanGiftCard', async ({code}, {getState, dispatch}) => {
  const {eGiftCardSuccesNavigation} = getDescriber();
  try {
    dispatch(showSpinner({}));
    const giftCard = await AccountApi.searchEGiftCard(
      code.replace('GMA:id:', ''),
    );
    Logger.Log.LogAPIEvent(
      'AccountAPI',
      'SearchEGiftCard',
      JSON.stringify({}),
      JSON.stringify(giftCard),
    );
    let isSuccess = false;
    let isSmaRestrict = false;
    if (giftCard.items.length > 0) {
      const giftCardDetail = giftCard.items.find((gift) => gift);
      const balance = giftCardDetail?.balances?.find((b) => b);
      //Validate GiftCard for SMA
      const isValidGiftCard = await Util.validateGiftCardForSma(
        getState().account.isHomeLocationSma,
        getState().account.account.orgId,
        balance?.orgId,
      );
      if (!isValidGiftCard) {
        isSuccess = false;
        isSmaRestrict = true;
      } else {
        if (
          balance?.expirationDate &&
          TimeUtils.isDateSmallerThanToday(balance.expirationDate)
        ) {
          isSuccess = false;
          alertError(Localized.Errors.eGift_card_expired);
        } else if (Number(balance?.balance) <= 0) {
          isSuccess = false;
          alertError(Localized.Errors.eGift_card_already_used);
        } else {
          const response = await AccountApi.transferEGiftCardBalance({
            giftCardBalanceId: balance?.accountBalanceId ?? '',
            giftCardAccountId: balance?.accountId ?? '',
            amount: balance?.balance ?? 0,
          }).catch(() => {
            alertError(Localized.Errors.funding_unavailable);
          });
          Logger.Log.LogAPIEvent(
            'AccountAPI',
            'TransferEGiftCardBalance',
            JSON.stringify({
              giftCardBalanceId: balance.accountBalanceId,
              giftCardAccountId: balance.accountId,
              amount: balance.balance,
            }),
            JSON.stringify(response),
          );

          if (response) {
            isSuccess = true;
            const newSuccessMessage = `$${balance?.balance} ${Localized.Success.gift_card_added}`;
            alertSuccess(newSuccessMessage, () => {
              eGiftCardSuccesNavigation();
            });
          }
        }
      }
    } else {
      isSuccess = false;
      alertError(Localized.Errors.failed_to_add_funds);
    }
    const _response = {
      isSuccess,
      isSmaRestrict,
    };
    return _response;
  } catch (error) {
    CrashlyticsEvents.log(
      'Exception',
      'accountSlice:scanGiftCard',
      generateErrorMessage(error),
    );
    Events.Error.trackEvent(
      'Exception',
      'accountSlice:scanGiftCard',
      generateErrorMessage(error),
    );
  } finally {
    dispatch(hideSpinner());
  }
});

export const fetchHomeLocation = createAsyncThunk(
  'account/fetchLocation',
  async (locationId: string) => {
    try {
      const location = await PlatformApi.fetchLocation(locationId);
      return location;
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'accountSlice:fetchLocation',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'accountSlice:fetchLocation',
        generateErrorMessage(error),
      );
      throw error;
    }
  },
);

export const fetchProductRecommendations = createAsyncThunk(
  'account/fetchProductRecommendations',
  async ({locationId, accountId}: {locationId: string; accountId: string}) => {
    try {
      return await AppApi.fetchProductRecommendations(locationId, accountId);
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'accountSlice:fetchLocation',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'accountSlice:fetchLocation',
        generateErrorMessage(error),
      );
      throw error;
    }
  },
);

const canAddFunds = (state: IAccountState): boolean => {
  return (
    state.creditCards.length > 0 ||
    state.account.isPayrollAvailable ||
    state.paymentCredentials.applePayAvailable ||
    state.paymentCredentials.googlePayAvailable
  );
};

const getDefaultPaymentMethodToken = (state: IAccountState): string => {
  const creditCardsFiltered = Util.filterExpiredCreditCards(state.creditCards);
  const hasCards = creditCardsFiltered.length > 0;

  if (state.account.isPayrollAvailable) {
    return AccountConstants.PAYROLL_TOKEN;
  } else if (state.paymentCredentials.applePayAvailable) {
    return AccountConstants.APPLE_PAY_TOKEN;
  } else if (state.paymentCredentials.googlePayAvailable) {
    return AccountConstants.GOOGLE_PAY_TOKEN;
  } else if (hasCards) {
    return state.creditCards[0].id || '';
  }

  return '';
};

const getDefaultNonNfcPaymentMethodToken = (state: IAccountState): string => {
  const hasCards = state.creditCards.length > 0;

  if (state.account.isPayrollAvailable) {
    return AccountConstants.PAYROLL_TOKEN;
  } else if (hasCards) {
    return state.creditCards[0].id || '';
  }

  return '';
};

const logBalanceChangedEvent = (
  state: IAccountState,
  source: string,
  newBalance: number,
) => {
  BalanceChangedEvent.trackEvent(source, newBalance?.toFixed(2));
};

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    logout(state) {
      state.creditCards = [];
      state.paymentCredentials = defaultPaymentCredentials;
      state.account = defaultAccount;
      state.canAddFunds = false;
      state.defaultNonNfcPaymentToken = '';
      state.defaultPaymentToken = '';
      state.payrollDeductInformation = null;
    },
    adjustDefaultBalance(
      state,
      action: PayloadAction<{amount: number; reason: string}>,
    ) {
      if (state.account.defaultBalance && !state.isHomeLocationSma) {
        const newBalance =
          state.account.defaultBalance.balance + action.payload.amount;
        state.account.defaultBalance.balance = Math.max(newBalance, 0);
        const newBalances = state.account.displayBalances.map((balance) => {
          if (balance.isDefault) {
            return {
              ...balance,
              balance: Math.max(newBalance, 0),
              lastChanged: new Date().toISOString(),
            };
          }
          return balance;
        });
        state.account.displayBalances = newBalances;
        logBalanceChangedEvent(
          state,
          action.payload.reason,
          state.account.defaultBalance.balance,
        );
      } else if (state.account.smaBalance && state.isHomeLocationSma) {
        const newSmaBalance =
          state.account.smaBalance.balance + action.payload.amount;
        state.account.smaBalance.balance = Math.max(newSmaBalance, 0);
        const newBalances = state.account.displayBalances.map((balance) => {
          if (balance.isSMA) {
            return {
              ...balance,
              balance: Math.max(newSmaBalance, 0),
              lastChanged: new Date().toISOString(),
            };
          }
          return balance;
        });
        //display balance locally calculated from newBalances
        state.account.displayBalances = newBalances;
        logBalanceChangedEvent(
          state,
          action.payload.reason,
          state.account.smaBalance.balance,
        );
      }
    },
    storeReceipt(state, action: PayloadAction<savedReceipt>) {
      state.savedReceipt = action.payload;
    },
    deleteReceipt(state) {
      state.savedReceipt = null;
    },
    setBeaconPopupOff(state) {
      state.beaconPopup = true;
    },
    adjustPoints(state, action: PayloadAction<{points: number}>) {
      if (state.account.points) {
        state.account.points -= action.payload.points;
      }
    },
    startPaymentFunding(state) {
      state.isPaymentFunding = true;
    },
    endPaymentFunding(state) {
      state.isPaymentFunding = false;
    },
  }, // used for sync actions
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountBalanceCreditCards.fulfilled, (state, action) => {
        state.creditCards = action.payload || [];
        state.defaultNonNfcPaymentToken =
          getDefaultNonNfcPaymentMethodToken(state);
        state.defaultPaymentToken = getDefaultPaymentMethodToken(state);
        state.canAddFunds = canAddFunds(state);
      })
      .addCase(fetchAccountBalanceCreditCards.rejected, (_, action) => {
        const error = action.error;
        CrashlyticsEvents.log(
          'Exception',
          'AccountSlice:FetchAccountBalanceCreditCards',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'AccountSlice:FetchAccountBalanceCreditCards',
          generateErrorMessage(error),
        );
      })
      .addCase(deleteCard.fulfilled, (state, action) => {
        state.creditCards = action.payload?.creditCards || [];
        state.lastDeletedCardWasDefault = action.payload?.isDefault || false;
        state.defaultNonNfcPaymentToken =
          getDefaultNonNfcPaymentMethodToken(state);
        state.defaultPaymentToken = getDefaultPaymentMethodToken(state);
        state.canAddFunds = canAddFunds(state);
      })
      .addCase(deleteCard.rejected, (_, action) => {
        const error = action.error;
        CrashlyticsEvents.log(
          'Exception',
          'AccountSlice:DeleteCard',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'AccountSlice:DeleteCard',
          generateErrorMessage(error),
        );
      })
      .addCase(setDefaultPaymentToken.fulfilled, (state, action) => {
        state.defaultPaymentToken = action.payload;
        state.defaultNonNfcPaymentToken =
          getDefaultNonNfcPaymentMethodToken(state);
      })
      .addCase(setDefaultPaymentToken.rejected, (_, action) => {
        const error = action.error;
        CrashlyticsEvents.log(
          'Exception',
          'AccountSlice:SetDefaultPaymentToken',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'AccountSlice:SetDefaultPaymentToken',
          generateErrorMessage(error),
        );
      })
      .addCase(fetchAccountBalances.fulfilled, (state, action) => {
        state.account.balances = action.payload;
        state.account.displayBalances = action.payload
          .filter(
            (balance) =>
              balance.type !== BalanceTypes.Payroll && !balance.isDisabled,
          )
          .map((newBalance) => {
            const oldBalance = state.account.displayBalances.find(
              (item) => item.id === newBalance.id,
            );

            if (oldBalance?.lastChanged) {
              const lastChanged = moment(oldBalance.lastChanged);
              const newUpdatedServer = moment(newBalance.utcLastUpdated);

              if (newUpdatedServer.isSameOrBefore(lastChanged)) {
                // If the server's last updated date is the same or before the last changed date, keep the old balance
                return oldBalance;
              }
            }

            return {
              ...newBalance,
              lastChanged: newBalance.utcLastUpdated,
            };
          });
      })
      .addCase(fetchAccountBalances.rejected, (_, action) => {
        const error = action.error;
        CrashlyticsEvents.log(
          'Exception',
          'AccountSlice:FetchAccountBalances',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'AccountSlice:FetchAccountBalances',
          generateErrorMessage(error),
        );
      })
      .addCase(payrollDeductOptInChanged.fulfilled, (state, action) => {
        state.account.isPayrollAvailable = Boolean(
          state.account.payrollIdentifier?.value &&
            state.account.payrollIdentifier?.value?.length > 0 &&
            action.payload,
        );
        state.account.isPayrollDeductOptIn = action.payload;
        state.canAddFunds = canAddFunds(state);
        state.defaultNonNfcPaymentToken =
          getDefaultNonNfcPaymentMethodToken(state);
        state.defaultPaymentToken = getDefaultPaymentMethodToken(state);
      })
      .addCase(payrollDeductOptInChanged.rejected, (_, action) => {
        const error = action.error;
        CrashlyticsEvents.log(
          'Exception',
          'AccountSlice:PayrollDeductOptInChanged',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'AccountSlice:PayrollDeductOptInChanged',
          generateErrorMessage(error),
        );
      })
      .addCase(fetchAccount.fulfilled, (state, action) => {
        if (!action.payload?.account) {
          return;
        }

        const newAccount = action.payload.account;
        const currentAccount = state.account;

        const currentHasValidData =
          currentAccount?.id &&
          currentAccount?.email &&
          currentAccount.id !== '';
        const newIsBroken =
          !newAccount ||
          !newAccount.id ||
          !newAccount.email ||
          newAccount.id === '' ||
          newAccount.email === '' ||
          newAccount === defaultAccount ||
          (newAccount.id === undefined && newAccount.email === undefined);

        if (currentHasValidData && newIsBroken) {
          // Keep existing valid data, don't overwrite with broken data
          return;
        }

        state.account = newAccount;
        state.creditCards = action.payload.creditCards;
        state.payrollDeductInformation =
          action.payload.payrollDeductInformation;
        state.pinRequired = action.payload.pinRequired;
        state.canAddFunds = canAddFunds(state);
        state.defaultNonNfcPaymentToken =
          getDefaultNonNfcPaymentMethodToken(state);
        state.defaultPaymentToken = getDefaultPaymentMethodToken(state);
      })
      .addCase(fetchAccount.rejected, (_, action) => {
        const error = action.error;
        CrashlyticsEvents.log(
          'Exception',
          'AccountSlice:FetchAccount',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'AccountSlice:FetchAccount',
          generateErrorMessage(error),
        );
      })
      .addCase(fetchPaymentCredentials.fulfilled, (state, action) => {
        if (!action.payload) {
          return;
        }
        state.paymentCredentials = action.payload;
        state.canAddFunds = canAddFunds(state);
        state.defaultNonNfcPaymentToken =
          getDefaultNonNfcPaymentMethodToken(state);
        state.defaultPaymentToken = getDefaultPaymentMethodToken(state);
      })
      .addCase(fetchPaymentCredentials.rejected, (_, action) => {
        const error = action.error;
        CrashlyticsEvents.log(
          'Exception',
          'AccountSlice:FetchPaymentCredentials',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'AccountSlice:FetchPaymentCredentials',
          generateErrorMessage(error),
        );
      })
      .addCase(fetchHomeLocation.fulfilled, (state, action) => {
        if (!action.payload) {
          return;
        }
        const locationFeatures = new LocationFeatures(
          action.payload.locationFeatures as Sfecfg,
        );
        state.isHomeLocationSma = locationFeatures.IsSma;
        state.payrollAutoComplete = Boolean(
          action.payload?.locationFeatures?.payrollAutoComplete,
        );
      })

      .addCase(fetchHomeLocation.rejected, (state, action) => {
        state.isHomeLocationSma = false;
        const error = action.error;
        CrashlyticsEvents.log(
          'Exception',
          'AccountSlice:FetchHomeLocation',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'AccountSlice:FetchHomeLocation',
          generateErrorMessage(error),
        );
      })
      .addCase(fetchProductRecommendations.fulfilled, (state, action) => {
        if (!action.payload) {
          state.productRecommendations = [];
          return;
        }
        state.productRecommendations = action.payload;
      })
      .addCase(fetchProductRecommendations.rejected, (_, action) => {
        const error = action.error;
        CrashlyticsEvents.log(
          'Exception',
          'AccountSlice:FetchProductRecommendations',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'AccountSlice:FetchProductRecommendations',
          generateErrorMessage(error),
        );
      })
      .addCase(updatePin.fulfilled, (state, action) => {
        state.pinRequired = action.payload.pinRequired;
      })
      .addCase(acceptPrivacyVersion.fulfilled, (state, action) => {
        state.privacyVersionResponse = {
          status: action.payload.status,
          version: action.payload.version,
          type: action.payload.type,
        };
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        if (state.account) {
          if (action.payload.firstName !== undefined) {
            state.account.firstName = action.payload.firstName;
          }
          if (action.payload.lastName !== undefined) {
            state.account.lastName = action.payload.lastName;
          }
          if (action.payload.locale !== undefined) {
            state.account.locale = action.payload.locale;
          }
          if (!state.account.demographics) {
            state.account.demographics = {} as any;
          }
          if (state.account.demographics) {
            if (action.payload.gender !== undefined) {
              state.account.demographics.gender = action.payload.gender;
            }
            if (action.payload.jobTitle !== undefined) {
              state.account.demographics.jobTitle = action.payload.jobTitle;
            }
            if (action.payload.birthMonth !== undefined) {
              state.account.demographics.birthMonth = Number(
                action.payload.birthMonth,
              );
            }
            if (action.payload.birthYear !== undefined) {
              state.account.demographics.birthYear = Number(
                action.payload.birthYear,
              );
            }
          }
        }
      });
  },
});
export const {
  logout,
  storeReceipt,
  deleteReceipt,
  setBeaconPopupOff,
  adjustDefaultBalance,
  adjustPoints,
  startPaymentFunding,
  endPaymentFunding,
} = accountSlice.actions;
export default accountSlice.reducer;
